'use client'

import * as React from 'react'
import { Check, ChevronDown, ChevronsUpDownIcon, ChevronUp } from 'lucide-react'
import { Select as SelectPrimitive } from 'radix-ui'

import { fieldVariants } from '@/components/ui/field-variants'
import { ChatContainerContext } from '@/features/order/chat/lib/chat-container-context'
import { cn } from '@/lib/utils'

const Select = SelectPrimitive.Root

/** Select field styling for searchable pickers hosted in a Popover. */
const SelectPopoverTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      fieldVariants(),
      'rounded-sm py-2 [&[aria-expanded=true]_svg]:rotate-180',
      className
    )}
    {...props}
  >
    <span className="flex w-full items-center justify-between gap-2">
      <span className="line-clamp-1">{children}</span>
      <ChevronDown className="text-foreground/60 size-4 shrink-0 transition-transform" />
    </span>
  </button>
))
SelectPopoverTrigger.displayName = 'SelectPopoverTrigger'

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    isUpdownIcon?: boolean
    readonly?: boolean
    hideIcon?: boolean
    containerClassName?: string
    // fieldVariants size; `xs` is the 24px dense-toolbar field.
    size?: 'xs' | 'sm' | 'default'
  }
>(
  (
    {
      className,
      size = 'default',
      children,
      isUpdownIcon = false,
      readonly = false,
      hideIcon = false,
      containerClassName,
      ...props
    },
    ref
  ) => (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        readonly
          ? 'flex items-center text-sm'
          : cn(
              fieldVariants({ size }),
              size === 'default' ? 'py-2' : 'py-0',
              'rounded-sm [&>span]:line-clamp-1 data-[state=open]:[&>svg]:rotate-180'
            ),
        className
      )}
      disabled={readonly ? true : props.disabled}
      {...props}
    >
      <div
        className={cn([
          'flex w-full items-center',
          readonly ? '' : 'justify-between gap-2',
          containerClassName,
        ])}
      >
        {children}
        {!readonly && !hideIcon && (
          <SelectPrimitive.Icon asChild>
            {isUpdownIcon ? (
              <ChevronsUpDownIcon
                className={cn(
                  'text-foreground/60 shrink-0 transition-all',
                  size === 'default' ? 'size-4' : 'size-3.5'
                )}
              />
            ) : (
              <ChevronDown
                className={cn(
                  'text-foreground/60 shrink-0 transition-all',
                  size === 'default' ? 'size-4' : 'size-3.5'
                )}
              />
            )}
          </SelectPrimitive.Icon>
        )}
      </div>
    </SelectPrimitive.Trigger>
  )
)
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

// The scroll chevrons float over the list's top/bottom edge (as in OverlayScrollY) instead of
// taking a row each: no gap above the first item, and nothing shifts as they come and go. The
// fade is the menu surface, so rows slide under the chevron.
const SCROLL_BUTTON_CLASS =
  'from-popover absolute inset-x-0 z-10 flex h-6 cursor-default items-center justify-center to-transparent'

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(SCROLL_BUTTON_CLASS, 'top-0 bg-linear-to-b', className)}
    {...props}
  >
    <ChevronUp className="text-foreground/40 h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(SCROLL_BUTTON_CLASS, 'bottom-0 bg-linear-to-t', className)}
    {...props}
  >
    <ChevronDown className="text-foreground/40 h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    footer?: React.ReactNode
    viewportClassName?: string
  }
>(({ className, children, footer, position = 'popper', viewportClassName, ...props }, ref) => {
  const chatContainer = React.useContext(ChatContainerContext)
  return (
    <SelectPrimitive.Portal container={chatContainer?.ownerDocument?.body}>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'menu-surface data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-[60] max-h-[--radix-select-content-available-height] min-w-32 origin-[--radix-select-content-transform-origin] overflow-x-hidden overflow-y-auto rounded-sm',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          footer && 'overflow-y-hidden',
          className
        )}
        position={position}
        {...props}
      >
        {/* Positions the chevrons over the list itself, not over a footer below it. */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              position === 'popper' &&
                'flex h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width) flex-col',
              footer && 'overflow-y-auto',
              viewportClassName
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </div>
        {footer}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('text-foreground/50 py-2 pr-3 pl-3 text-xs font-medium', className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    showCheck?: boolean
    /** Trailing control beside the label (e.g. a favorite star); kept out of ItemText, so it
     *  never shows in a SelectValue. Stops its own presses so they don't pick the item. */
    endSlot?: React.ReactNode
    /** Passive trailing text (a count, a unit) — also kept out of ItemText; a press on it
     *  still picks the item. */
    hint?: React.ReactNode
  }
>(({ className, children, showCheck = false, endSlot, hint, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'text-foreground/80 data-[state=checked]:bg-foreground/5 data-[state=checked]:text-foreground focus:bg-foreground/10 focus:text-foreground relative flex w-full cursor-default items-center px-3 py-2 text-sm outline-none select-none not-disabled:cursor-pointer data-disabled:pointer-events-none data-disabled:opacity-50',
      className
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        {showCheck && <Check className="text-chart-mint size-3" />}
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    {hint && <span className="text-foreground/40 ml-auto shrink-0 pl-3 text-[11px]">{hint}</span>}
    {endSlot && (
      <span
        className="ml-auto flex shrink-0 items-center pl-3"
        onPointerDown={(event) => event.stopPropagation()}
        onPointerUp={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        {endSlot}
      </span>
    )}
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('bg-foreground/10 my-1 h-px', className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectPopoverTrigger,
  SelectValue,
}
